import { ActionReducerMapBuilder, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { AuthResult, googleLogin } from '../../services/AuthService';
import { AuthState } from './AuthState';
import { LoginResponseLocalStorageKey, setAuthHeaderToDefaultHeader } from '../../services/BackendService';
import { clearItem, storeItem } from '../../services/LocalStorageService';
import { RejectError, RejectErrorType } from '../error/Error';
import { APIError } from '../../services/HttpService';
import { createDefaultMatchers, SliceStatus } from '../BaseState';
import { WritableDraft } from 'immer/dist/internal';

const initialState: AuthState = {
  authResult: null,
  pendings: [],
  status: SliceStatus.idle,
  error: null,
};

export const login = createAsyncThunk<AuthResult, string, { rejectValue: RejectError }>(
  'auth/login',
  async (token: string, { rejectWithValue }) => {
    try {
      const authResult: AuthResult = await googleLogin(token);
      return authResult;
    } catch (error) {
      if (error instanceof APIError) {
        return rejectWithValue({ message: error.message, code: error.statusCode, type: RejectErrorType.API });
      }

      throw error;
    }
  },
  {
    condition: (token: string, { getState }: { getState: () => RootState }): boolean => {
      const auth = getState().auth as AuthState;
      if (auth.authResult || auth.status === SliceStatus.loading) {
        // Already fetched or in progress, don't need to re-fetch
        return false;
      }

      return true;
    },
  }
);

const doSetAuthResult = (draft: WritableDraft<AuthState>, action: PayloadAction<AuthResult | null>) => {
  const authResult = action.payload;
  draft.authResult = authResult;

  if (authResult) {
    storeItem(LoginResponseLocalStorageKey, authResult, new Date(authResult.expires));
    setAuthHeaderToDefaultHeader();
  } else {
    clearItem(LoginResponseLocalStorageKey);
  }
};

const doLogout = (draft: WritableDraft<AuthState>) => {
  draft.authResult = null;
  clearItem(LoginResponseLocalStorageKey);
};

const createActions = (builder: ActionReducerMapBuilder<AuthState>) => {
  builder.addCase(login.fulfilled, doSetAuthResult);

  createDefaultMatchers(login.typePrefix, builder);
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    doSetAuthResult,
    doLogout,
  },
  extraReducers: createActions,
});

export const { doSetAuthResult: setAuthResult, doLogout: logout } = authSlice.actions;
export const authState = (state: RootState): AuthState => state.auth;
export default authSlice.reducer;
